import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cva } from 'class-variance-authority';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { Button } from '@rocco/components/button';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';
import { formatDateToDDMMYY } from '@rocco/utils/format-date';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { trackTileMediaDetailClick } from '@archipro-website/web/app/modules/tracking/util/trackTileMedia';
import { useTracker } from '../../../../../tracker/src/react-bindings/context';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';

const _articleTileContentVariants = cva('', {
    variants: {
        variant: {
            featured:
                'absolute inset-x-0 bottom-0 flex items-end justify-between gap-18 px-[1.83rem] pb-[2.12rem] text-gray-50',
            default: 'pt-4 text-[#515151]',
        },
    },
    defaultVariants: {
        variant: 'featured',
    },
});

const _articleTileTitleVariants = cva(
    'text-base font-medium tracking-[0.02rem]',
    {
        variants: {
            variant: {
                featured: 'pr-[5rem]',
                default: 'line-clamp-3 text-gray-900',
            },
        },
        defaultVariants: {
            variant: 'featured',
        },
    },
);

const _articleButtonVariants = cva('', {
    variants: {
        variant: {
            featured: '',
            default: 'hidden',
        },
    },
});

export const ArticleTile = (props: TileViewModel<ArticleTileViewModel>) => {
    const {
        aspectRatio,
        model: article,
        enableTileSlider,
        isFollowed,
        onFollow,
        routeState,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        section,
    } = props;
    const { variant = 'featured' } = article;
    const tracker = useTracker();
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);

    const handleTileClick = () => {
        onClick?.();
        if (disableLink) {
            return;
        }
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            article.id,
            article.professional.id,
            section,
        );
        trackTileMediaDetailClick(tracker, {
            itemID: article.id,
            itemType: 'ArticleTile',
            itemName: article.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={article.link}
            prefetch="intent"
            state={routeState}
            onClick={handleTileClick}
        >
            <div className="h-full">
                <div className={cn('flex h-full flex-col gap-4', className)}>
                    <div className="group relative h-full">
                        <TileMedia
                            aspectRatio={aspectRatio}
                            images={[article.image]}
                            video={article.video}
                            enableTileSlider={enableTileSlider}
                            gradiantOverlay={true}
                        />
                        {overlay ? (
                            overlay
                        ) : (
                            <div className="absolute right-4 top-3.5 flex gap-1 md:invisible md:group-hover:visible">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton && (
                                          <TileFollowButton
                                              pinId={article.id}
                                              pinType="ArticlePin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        )}

                        {article.badge && (
                            <div className="absolute left-4 top-4">
                                <TileBadge title="Article" />
                            </div>
                        )}

                        <div
                            className={cn(
                                _articleTileContentVariants({ variant }),
                                TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            )}
                        >
                            <div className="grid w-full grid-cols-[1fr_auto] items-end gap-2">
                                <div className="flex flex-col gap-3 md:gap-[0.5625rem]">
                                    <span
                                        className={cn(
                                            _articleTileTitleVariants({
                                                variant,
                                            }),
                                            contentEditable?.enabled.title &&
                                                'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                                        )}
                                        contentEditable={
                                            contentEditable?.enabled.title
                                        }
                                        role={
                                            contentEditable?.enabled.title
                                                ? 'textbox'
                                                : undefined
                                        }
                                        aria-label={
                                            contentEditable?.enabled.title
                                                ? 'Edit title'
                                                : undefined
                                        }
                                        aria-multiline="false"
                                        suppressContentEditableWarning={true}
                                        onInput={(
                                            e: React.FormEvent<HTMLSpanElement>,
                                        ) =>
                                            contentEditable?.onChange?.(
                                                (e.target as HTMLSpanElement)
                                                    .textContent ?? '',
                                                'title',
                                            )
                                        }
                                        onBlur={() => {
                                            contentEditable?.onBlur?.();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLSpanElement>,
                                        ) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                (
                                                    e.target as HTMLSpanElement
                                                ).blur();
                                            }
                                        }}
                                    >
                                        {article.title}
                                    </span>
                                    <span
                                        className={cn(
                                            'line-clamp-1 text-ellipsis text-3.5 font-normal leading-[1.1375rem] tracking-[0.0175rem] md:text-[0.875rem]',
                                            contentEditable?.enabled
                                                .description &&
                                                'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                                        )}
                                        contentEditable={
                                            contentEditable?.enabled.description
                                        }
                                        role={
                                            contentEditable?.enabled.description
                                                ? 'textbox'
                                                : undefined
                                        }
                                        aria-label={
                                            contentEditable?.enabled.description
                                                ? 'Edit description'
                                                : undefined
                                        }
                                        aria-multiline="false"
                                        suppressContentEditableWarning={true}
                                        onInput={(
                                            e: React.FormEvent<HTMLSpanElement>,
                                        ) =>
                                            contentEditable?.onChange?.(
                                                (e.target as HTMLSpanElement)
                                                    .textContent ?? '',
                                                'description',
                                            )
                                        }
                                        onBlur={() => {
                                            contentEditable?.onBlur?.();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLSpanElement>,
                                        ) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                (
                                                    e.target as HTMLSpanElement
                                                ).blur();
                                            }
                                        }}
                                    >
                                        {article.date
                                            ? `${formatDateToDDMMYY(article.date)} - `
                                            : ''}
                                        Written by {article.label}
                                    </span>
                                </div>
                                <div
                                    className={_articleButtonVariants({
                                        variant,
                                    })}
                                >
                                    <Button
                                        size="square42"
                                        color="primary"
                                        aria-label="Read article"
                                    >
                                        <ArrowRightIcon />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {inlineActionButtons && (
                    <div className="mt-4 flex flex-col">
                        {inlineActionButtons}
                    </div>
                )}
            </div>
        </OptionalLink>
    );
};
